import Colors from '@constants/Colors';
import { SessionStatus, type SessionOutput } from '@sunrise-ui/api/events/api';
import { SessionCard } from '@sunrise-ui/composites';
import {
  Button,
  ButtonVariants,
  TextBold,
  TextReg,
  useDynamicStyles,
  type WithResponsive,
} from '@sunrise-ui/primitives';
import { DateTime } from 'luxon';
import React, { useMemo, useState } from 'react';
import { StyleSheet, View } from 'react-native';

const MAX_VISIBLE_SESSIONS = 3;

type Props = {
  eventId: string;
  sessions: SessionOutput[];
  isLoading?: boolean;
  communityId?: string;
};

/**
 * EventAgendaSection - Displays session cards for an event
 *
 * Shows first 3 sessions sorted by start time, with a "See All" button
 * to reveal remaining sessions. For single-session events, this section
 * may be hidden by the parent component.
 */
export function EventAgendaSection({
  eventId,
  sessions,
  isLoading = false,
  communityId,
}: Props) {
  const styles = useDynamicStyles(createStyles);
  const [showAll, setShowAll] = useState(false);

  // Filter to only confirmed sessions, then sort by start_date ascending
  const sortedSessions = useMemo(() => {
    if (!sessions || sessions.length === 0) return [];

    const confirmedSessions = sessions.filter(
      (session) => session.status === SessionStatus.Confirmed,
    );

    return [...confirmedSessions].sort((a, b) => {
      const dateA = a.start_date ? DateTime.fromISO(a.start_date) : null;
      const dateB = b.start_date ? DateTime.fromISO(b.start_date) : null;

      if (!dateA && !dateB) return 0;
      if (!dateA) return 1;
      if (!dateB) return -1;

      return dateA.toMillis() - dateB.toMillis();
    });
  }, [sessions]);

  // Determine which sessions to show
  const visibleSessions = useMemo(() => {
    if (showAll) return sortedSessions;
    return sortedSessions.slice(0, MAX_VISIBLE_SESSIONS);
  }, [sortedSessions, showAll]);

  const hasMoreSessions = sortedSessions.length > MAX_VISIBLE_SESSIONS;

  const handleToggleShowAll = () => {
    setShowAll((prev) => !prev);
  };

  // Don't render section if no sessions
  if (!isLoading && sortedSessions.length === 0) {
    return null;
  }

  // Loading state
  if (isLoading) {
    return (
      <View style={styles.container}>
        <TextBold style={styles.sectionTitle}>Agenda</TextBold>
        <View style={styles.loadingContainer}>
          <TextReg style={styles.loadingText}>Loading sessions...</TextReg>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <TextBold style={styles.sectionTitle}>Agenda</TextBold>

      <View style={styles.sessionsList}>
        {visibleSessions.map((session) => (
          <SessionCard
            key={session.id}
            eventId={eventId}
            sessionId={session.id ?? ''}
            communityId={communityId}
            style={styles.sessionCard}
          />
        ))}
      </View>

      {/* See All / Show Less Button */}
      {hasMoreSessions && (
        <View style={styles.toggleButtonContainer}>
          <Button
            size="small"
            variant={ButtonVariants.WHITE}
            onPress={handleToggleShowAll}
          >
            <TextReg>
              {showAll
                ? 'Show Less'
                : `See All ${sortedSessions.length} Sessions`}
            </TextReg>
          </Button>
        </View>
      )}
    </View>
  );
}

const createStyles = ({ isMobile }: WithResponsive) =>
  StyleSheet.create({
    container: {
      width: '100%',
      paddingHorizontal: isMobile ? 16 : 0,
      gap: isMobile ? 16 : 20,
    },
    sectionTitle: {
      fontSize: isMobile ? 20 : 24,
      color: Colors.dark.text,
      textAlign: 'left',
    },
    sessionsList: {
      width: '100%',
      gap: isMobile ? 12 : 16,
      alignItems: 'flex-start',
    },
    sessionCard: {
      width: '100%',
      maxWidth: isMobile ? 600 : undefined,
    },
    toggleButtonContainer: {
      alignItems: 'flex-start',
      marginTop: isMobile ? 8 : 12,
    },
    loadingContainer: {
      alignItems: 'center',
      padding: 20,
    },
    loadingText: {
      color: Colors.dark.text,
      opacity: 0.7,
    },
  });
